/* eslint-disable react/jsx-no-target-blank */
import React, { useState, useEffect } from "react"
import { useSelector } from "react-redux"
import _isEmpty from "lodash/isEmpty"
import _isEqual from "lodash/isEqual"
import useIsSsr from "@/hooks/useIsSsr"
import {
  convertStringToBoolean,
  createIdFromString,
  getRandom,
  getShortenedUrl,
  sanitizeInnerHtml,
  pushAnalyticsObjToDataLayer,
  getShortenedUrlwithGeneral,
} from "@/utils/helper"
import { getConfig } from "@/constants/config"
import { ConditionalWrapper } from "@/utils"
import DynamicImage from "@/components/core/Image/DynamicImage"
import Description from "@/components/Default/Teaser/v1/Description"
import Action from "@/components/Default/Teaser/v1/Action"
import {
  COMPONENT_TYPES,
  EMPTY_PIXEL,
  TEASER_CAPTION_CLASSES_LIST,
  TEASER_CLASSES,
} from "@/constants"
import Utilities from "@/components/Default/Teaser/v1/Utilities"
import { selectGenericState } from "@/store/features/genericSlice"
import {
  addTeaserLinkAnalytics,
  addTeaserAnalytic,
  getTitleEventInfo,
  getCaptionLinkEventInfo,
} from "@/components/Default/Teaser/v1/analytics"
import { isProductFeatureLandscapeCarouselUpdatePage } from "../../CarouselSlick/v1/SlickSettings"
import { isPageNameFindAProResults } from "@/components/Default/Teaser/v1/helper"
import { useConfig } from "@/hooks/useConfig"
import {
  getManualSmartCropBaseUrl,
  hasManualSmartCropItems,
  isManualSmartCropEnabled,
} from "@/components/core/Image/manualSmartCrop"

const Teaser = props => {
  const [dynamicId, setDynamicId] = useState("")
  const { pageIsInteractive, datalayerPageName } =
    useSelector(selectGenericState)
  const generalConfigData = useConfig()
  const {
    data: {
      id = "",
      appliedCssClassNamesCustom = "",
      linkURLRef = false,
      linkNewTab = false,
      linkAll = false,
      linkTitle = false,
      linkImage = false,
      title = "",
      description = "",
      enableFav = false,
      enableShare = false,
      isDecorativeTeaser = false,
      mainTitle = "",
      componentPosition = "",
      pageType = "",
      linkDescription = "",
      linkURL = "",
      caption = "",
      datalayerLinkName = "",
      componentLinkType = "",
      eventName = "",
      eventAction = "",
      actions = [],
      downLoadLink = null,
      expandText = "",
      collapseText = "",
      disableExpandCollapse = "",
      imageResource: {
        imageServerUrl = "",
        assetID = "",
        pretitle: preTitle = "",
        urlModifiers = "",
        alt = "",
      } = {},
      enableManualSmartCrop = false,
      items = [],
      smartCrops = [],
      defaultImageURL = "",
      imageURLWithoutCrop = "",
      defaultRenditions = "",
      imageModifiersScene7 = "",
      utilities = [],
      utilitiesEnabled = false,
      dataLayerLinkType = "",
    } = {},
    fullData = {},
    showEmptyPlaceholder = false,
    maxHeight = "",
    containerId = "",
    pageName = "",
    bodytitle = "",
  } = props
  const generatedLinkURL = getShortenedUrlwithGeneral(
    linkURL,
    generalConfigData
  )

  const [parentActionLink, setParentActionLink] = useState(generatedLinkURL)
  const [componentGroup, setComponentGroup] = useState([])
  const [appliedCssClassNamesCustoms, setAppliedCssClassNamesCustoms] =
    useState([])
  const [columnClassNames, setColumnClassNames] = useState("")
  const [general, setGeneral] = useState(generalConfigData)
  const [innerCaption, setInnerCaption] = useState(false)
  const [isInsideImage, setIsInsideImage] = useState(false)
  const [activeIndex, setActive] = useState(0)
  const [fallbackDynamicId] = useState(() =>
    createIdFromString(getRandom() + "")
  )

  const isSsr = useIsSsr()
  const isMobile = typeof window !== "undefined" && window.innerWidth <= 768 // Example condition for mobile detection
  const isManualSmartCrop = isManualSmartCropEnabled(enableManualSmartCrop)
  const manualSmartCropItems = items.length ? items : smartCrops
  const manualSmartCropBaseUrl = getManualSmartCropBaseUrl({
    imageURLWithoutCrop,
    defaultImageURL,
    imageServerUrl,
    assetID,
  })
  const hasManualSmartCrops =
    isManualSmartCrop &&
    hasManualSmartCropItems(manualSmartCropItems, manualSmartCropBaseUrl)
  const isCaptionOnly = Boolean(
    title ||
      description ||
      actions?.length ||
      preTitle ||
      assetID ||
      showEmptyPlaceholder
  )
  // Decorative images must be ignored by assistive technology (WCAG 1.1.1)
  const imageAltText = convertStringToBoolean(isDecorativeTeaser) ? "" : alt
  const containerIdName = containerId ? containerId.replace(/-/g, " ") : "zone"

  useEffect(() => {
    fetchConfig()
    updateDynamicId()
    updateParentActionLink()
    updateComponentGroup()
    updateInnerCaption()
    checkUtilityPosition()
  }, [
    assetID,
    linkURL,
    fullData,
    appliedCssClassNamesCustom,
    isSsr,
    pageIsInteractive,
  ])

  useEffect(() => {
    updateColumnClassNames()
  }, [componentGroup, appliedCssClassNamesCustoms])

  const fetchConfig = async () => {
    if (_isEmpty(generalConfigData)) {
      const config = await getConfig()
      setGeneral(config?.general)
    }
  }

  const updateDynamicId = () => {
    const nextDynamicId = assetID
      ? createIdFromString(assetID)
      : id
      ? createIdFromString(id)
      : fallbackDynamicId

    setDynamicId(prev => (prev === nextDynamicId ? prev : nextDynamicId))
  }

  const updateParentActionLink = async () => {
    if (linkURL) {
      const res = await getShortenedUrl(linkURL)
      const [resBeforeHash, resAfterHash] = (res ?? "").split("#")
      setParentActionLink(
        resBeforeHash && resAfterHash
          ? `${resBeforeHash}#${encodeURIComponent(resAfterHash)}`
          : res
      )
    }
  }

  const updateComponentGroup = () => {
    if (!_isEmpty(fullData)) {
      const newCssClassNames = Object.keys(fullData).map(
        key => fullData[key]["appliedCssClassNamesCustom"]
      )
      const newComponentGroup = Object.keys(fullData).map(
        key => fullData[key][":type"]
      )
      setAppliedCssClassNamesCustoms(prev =>
        _isEqual(prev, newCssClassNames) ? prev : newCssClassNames
      )
      setComponentGroup(prev =>
        _isEqual(prev, newComponentGroup) ? prev : newComponentGroup
      )
    }
  }

  const updateColumnClassNames = () => {
    let classNameCustom = ""
    if (componentGroup.length > 0) {
      if (shouldAddHeroBannerClasses()) {
        classNameCustom += addHeroBannerClasses()
      }
    }
    setColumnClassNames(prev =>
      prev === classNameCustom ? prev : classNameCustom
    )
  }

  const shouldAddHeroBannerClasses = () => {
    return (
      componentGroup.some(a => a?.includes(COMPONENT_TYPES.TEASER)) &&
      appliedCssClassNamesCustoms.some(a =>
        a?.includes("hero-banner-article-top")
      ) &&
      !appliedCssClassNamesCustoms.some(a =>
        a?.includes("hero-banner-article-bottom")
      )
    )
  }

  const addHeroBannerClasses = () => {
    let classNameCustom = ""
    classNameCustom += " hero-banner-top-only "

    if (componentGroup.some(a => a?.includes(COMPONENT_TYPES.BREADCRUMB))) {
      classNameCustom += " paddingbasedbreadcrum "
    } else {
      classNameCustom += " without-breadcrumb "
    }
    return classNameCustom
  }

  const updateInnerCaption = () => {
    if (!isSsr && pageIsInteractive) {
      const hasInnerCaption = TEASER_CAPTION_CLASSES_LIST.some(classNames =>
        classNames
          .split(" ")
          .every(className =>
            appliedCssClassNamesCustom?.trim()?.includes(className)
          )
      )
      setInnerCaption(prev =>
        prev === hasInnerCaption ? prev : hasInnerCaption
      )
    }
  }

  const checkUtilityPosition = () => {
    const isInsideImage = TEASER_CLASSES.some(item =>
      appliedCssClassNamesCustom.includes(item)
    )
    setIsInsideImage(prev => (prev === isInsideImage ? prev : isInsideImage))
  }

  const getDataLayerLinkType = () => {
    return mainTitle || title
      ? `${pageType}:${mainTitle || title}`
      : dataLayerLinkType
  }

  const renderImage = () => {
    const renderDynamicImage = () => {
      const imageStyle =
        maxHeight && showEmptyPlaceholder && !assetID ? { maxHeight } : {}
      const imageUrl = assetID ? `${imageServerUrl}${assetID}` : EMPTY_PIXEL

      return (
        <DynamicImage
          style={imageStyle}
          className="fluidimage cmp-image__image"
          imgUrl={imageUrl}
          alt={imageAltText}
          itemProp="contentUrl"
          uniqueId={dynamicId}
          urlModifiers={urlModifiers}
          pageType={pageType}
          enableManualSmartCrop={enableManualSmartCrop}
          smartCropItems={manualSmartCropItems}
          manualSmartCropBaseUrl={manualSmartCropBaseUrl}
          defaultImageURL={defaultImageURL}
          defaultRendition={defaultRenditions}
          imageModifiersScene7={imageModifiersScene7}
        />
      )
    }

    const renderUtilities = () => {
      if (isInsideImage && utilitiesEnabled && utilities?.length > 0) {
        return (
          <Utilities
            utilities={utilities}
            title={title}
            assetID={assetID}
            imageServerUrl={imageServerUrl}
            alt={alt}
            pageType={pageType}
            dataLayerLinkType={getDataLayerLinkType()}
            mainTitle={mainTitle}
            downLoadLink={downLoadLink}
            containerId={containerId}
          />
        )
      }
      return null
    }

    const renderCaption = () => {
      if (innerCaption && caption) {
        return (
          <div className="cmp-img-caption">{sanitizeInnerHtml(caption)}</div>
        )
      }
      return null
    }

    const renderImageContent = () => {
      if (hasManualSmartCrops || assetID || showEmptyPlaceholder) {
        return renderDynamicImage()
      }
      return null
    }

    return (
      <div className="cq-dd-image cmp-teaser__image">
        <div className="cmp-image">
          <div className="s7responsiveContainer cmp-teaser__image cmp-image__image">
            <ConditionalWrapper
              condition={linkImage}
              wrapper={children => (
                <a
                  href={parentActionLink}
                  target={linkNewTab ? "_blank" : "_self"}
                  data-gbh-clickable="true"
                  rel="noreferrer"
                  onClick={() => {
                    addTeaserLinkAnalytics(
                      fullData,
                      parentActionLink,
                      bodytitle,
                      containerId,
                      props.data,
                      datalayerPageName,
                      fullData?.teaser?.datalayerLinkName
                    )
                  }}
                >
                  {children}
                </a>
              )}
            >
              {renderImageContent()}
            </ConditionalWrapper>
          </div>
        </div>
        {renderUtilities()}
        {renderCaption()}
      </div>
    )
  }

  const renderContent = () => (
    <div
      className="cmp-teaser__content"
      style={
        isMobile &&
        !isProductFeatureLandscapeCarouselUpdatePage(pageName) &&
        !isPageNameFindAProResults(pageName)
          ? { width: "100%" }
          : {}
      }
    >
      {preTitle && (
        <div className="cmp-teaser__pretitle">
          {sanitizeInnerHtml(preTitle)}
        </div>
      )}
      {title && (
        <div className="cmp-teaser__title" id={id}>
          <ConditionalWrapper
            condition={linkTitle}
            wrapper={children => (
              <a
                href={parentActionLink}
                rel={linkURLRef ? "noreferrer" : ""}
                target={linkNewTab ? "_blank" : "_self"}
                data-gbh-data-layer={getTitleEventInfo(
                  linkNewTab,
                  componentPosition,
                  linkURL,
                  mainTitle,
                  pageType,
                  componentLinkType,
                  title
                )}
                data-gbh-clickable="true"
              >
                {children}
              </a>
            )}
          >
            {sanitizeInnerHtml(title)}
          </ConditionalWrapper>
        </div>
      )}
      {description && (
        <Description
          linkURLRef={linkURLRef}
          general={general}
          linkNewTab={linkNewTab}
          description={description}
          mainTitle={mainTitle}
          componentPosition={componentPosition}
          pageType={pageType}
          linkDescription={linkDescription}
          expandText={expandText}
          collapseText={collapseText}
          disableExpandCollapse={disableExpandCollapse}
          preTitle={preTitle}
          linkURL={linkURL}
        />
      )}
      {actions?.length ? (
        <div className="cmp-teaser__action-container">
          {actions.map((action, index) => (
            <Action
              key={`teaser-action-${index + 1}-${getRandom()}`}
              playVideoCTA={convertStringToBoolean(action.playVideoCTA)}
              primaryCTA={convertStringToBoolean(action.primaryCTA)}
              secondaryCTA={convertStringToBoolean(action.secondaryCTA)}
              link={action.url}
              text={action.title}
              newTab={convertStringToBoolean(action.newTab)}
              urlRef={action.urlRef}
              id={id}
              dataLayerLinkType={dataLayerLinkType}
              pageType={pageType}
              linkAll={convertStringToBoolean(linkAll)}
              mainTitle={mainTitle}
              index={index}
              activeIndex={activeIndex}
              setActive={setActive}
            />
          ))}
        </div>
      ) : null}
      {!isInsideImage && utilitiesEnabled && utilities?.length > 0 && (
        <Utilities
          utilities={utilities}
          title={title}
          assetID={assetID}
          imageServerUrl={imageServerUrl}
          alt={alt}
          pageType={pageType}
          dataLayerLinkType={getDataLayerLinkType()}
          mainTitle={mainTitle}
          downLoadLink={downLoadLink}
          containerId={containerId}
        />
      )}
    </div>
  )

  const handleCaptionLinkClick = event => {
    const linkElement = event.target.closest("a")
    if (!linkElement) return

    const linkText = linkElement.textContent?.trim()
    const linkHref = linkElement.getAttribute("href")

    if (linkText && linkHref) {
      const eventInfo = getCaptionLinkEventInfo(pageType, linkText, linkHref)

      if (eventInfo && Object.keys(eventInfo).length > 0) {
        pushAnalyticsObjToDataLayer(eventInfo, "cmp:click")
      }
    }
  }

  return (
    <div
      className={`teaser ${
        appliedCssClassNamesCustom || ""
      }${columnClassNames}`}
      data-manual-smart-crop={hasManualSmartCrops ? "true" : undefined}
    >
      {isCaptionOnly && (
        <div
          className="cmp-teaser"
          data-gbh-data-layer={addTeaserAnalytic(
            linkNewTab,
            componentPosition,
            linkURL,
            mainTitle,
            pageType,
            componentLinkType,
            title,
            datalayerLinkName,
            containerIdName,
            eventName,
            eventAction
          )}
        >
          <ConditionalWrapper
            condition={linkAll}
            wrapper={children => (
              <a
                href={parentActionLink}
                target={linkNewTab ? "_blank" : "_self"}
                data-gbh-clickable="true"
                rel="noreferrer"
                onClick={() => {
                  addTeaserLinkAnalytics(
                    fullData,
                    parentActionLink,
                    bodytitle,
                    containerId,
                    props.data,
                    datalayerPageName,
                    fullData?.teaser?.datalayerLinkName
                  )
                }}
              >
                {children}
              </a>
            )}
          >
            {renderImage()}
            {(title ||
              description ||
              enableFav ||
              enableShare ||
              actions?.length ||
              preTitle) &&
              renderContent()}
          </ConditionalWrapper>
        </div>
      )}
      {!innerCaption && caption ? (
        <div className="cmp-img-caption" onClick={handleCaptionLinkClick}>
          {sanitizeInnerHtml(caption)}
        </div>
      ) : null}
    </div>
  )
}

export default Teaser
